import React, { useEffect, useState, useContext } from "react";
import { AppContext } from "src/context";
import {
  Form,
  Input,
  Upload,
  Button,
  DatePicker,
  Select,
  Row,
  Col,
  Typography,
  Divider,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import apiRequest from "src/common/utils/apiRequest";
import { SelectType } from "src/common/types";
import coreFunctions from "src/common/utils/coreFunctions";
import lang from "src/lang";
import DataFormatHelper from "src/common/utils/DataFormatHelper";
import LoadingPreview from "src/common/components/LoadingPreview";

const { Option } = Select;
const { Title, Text } = Typography;

const ProfileSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [departments, setDepartments] = useState<SelectType[]>([]);
  const [languages, setLanguages] = useState<SelectType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [signatureImage, setSignatureImage] = useState<string | null>(null);

  const CORE_FUNCTIONS = new coreFunctions();
  const { dispatch } = useContext(AppContext);

  const onFinish = async () => {
    setSaving(true);

    try {
      await form.validateFields();

      const API_REQUEST = new apiRequest(
        "user/profile/setting/general/edit",
        true
      );

      const values = DataFormatHelper.payloadData(
        form.getFieldsValue(),
        ["department", "language", "gender", "work_location"],
        [
          {
            field: "dob",
            with_time: false,
          },
        ]
      );

      await API_REQUEST.send(
        values,
        (response: { status: string; message: string; user: string }) => {
          if (response.status == "success") {
            if (values.profile_photo) {
              dispatch({
                type: "TRIGGER_UPLOAD_DATA",
                data: {
                  files: values.profile_photo,
                  type: "users_profile_image",
                  value: response.user,
                  delete_previous: true,
                  callback: (data: { source: string }) => {
                    setProfileImage(data.source);
                    localStorage.setItem("profile_image", data.source || "");
                  },
                },
              });
            }

            if (values.signature) {
              dispatch({
                type: "TRIGGER_UPLOAD_DATA",
                data: {
                  files: values.signature,
                  type: "users_profile_signature",
                  value: response.user,
                  delete_previous: true,
                },
              });
            }

            message.success(response.message);
          } else {
            message.error(response.message);
          }
        }
      );
    } catch (errorInfo: any) {
      message.error(errorInfo.errorFields[0].errors[0]);
    }
    setSaving(false);
  };

  const normFile = (e: any, type: string = "profile_photo") => {
    if (Array.isArray(e)) {
      return e;
    }
    if (e?.fileList?.length) {
      const reader = new FileReader();
      reader.onload = () => {
        if (type == "profile_photo") {
          setProfileImage(reader.result as string);
        }
        if (type == "signature") {
          setSignatureImage(reader.result as string);
        }
      };
      reader.readAsDataURL(e.fileList[0].originFileObj);
    } else {
      setProfileImage(null);
    }

    return e?.fileList || [];
  };

  useEffect(() => {
    const getData = () => {
      const API_REQUEST = new apiRequest(
        "user/profile/setting/general/get",
        true
      );

      API_REQUEST.send(
        {},
        (response: { status: string; data: any; message: string }) => {
          if (response.status === "success") {
            const values = { ...response.data };

            setProfileImage(values.profile_photo);
            setSignatureImage(values.signature);

            delete values.profile_photo;
            delete values.signature;

            form.setFieldsValue({
              ...values,
              dob: dayjs(response.data.dob),
            });

            setLoading(false);
          } else {
            message.error(response.message);
          }
        }
      );
    };

    getData();
  }, []);

  return (
    <div>
      <Title level={3} style={{ marginTop: "0" }}>
        Profile Settings
      </Title>
      <Text type="secondary">Manage your profile settings</Text>
      <Divider />

      {loading ? (
        <LoadingPreview />
      ) : (
        <Form
          form={form}
          name="profile_settings"
          onFinish={onFinish}
          layout="vertical"
        >
          <Row gutter={[24, 5]}>
            {/* Left Column */}
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item
                style={{ textAlign: "center" }}
                labelCol={{ style: { textAlign: "center" } }}
                label="Profile Photo"
                name="profile_photo"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  return normFile(e, "profile_photo");
                }}
              >
                <Upload
                  style={{ width: "100%" }}
                  name="profile_photo"
                  listType="picture-card"
                  showUploadList={false}
                  beforeUpload={() => false}
                >
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile Photo"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <div style={{ width: "100%" }}>
                      <UploadOutlined style={{ fontSize: "32px" }} />
                      <div style={{ marginTop: 8, width: "100%" }}>
                        Upload Profile Photo
                      </div>
                    </div>
                  )}
                </Upload>
              </Form.Item>

              {/* Name */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="first_name"
                    label="First Name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your first name",
                      },
                    ]}
                  >
                    <Input placeholder="First Name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="last_name"
                    label="Last Name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your last name",
                      },
                    ]}
                  >
                    <Input placeholder="Last Name" />
                  </Form.Item>
                </Col>
              </Row>

              {/* Email */}
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input placeholder="Email Address" />
              </Form.Item>

              {/* Phone Number */}
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[
                  { required: true, message: "Please enter your phone number" },
                ]}
              >
                <Input placeholder="Phone Number" />
              </Form.Item>

              {/* Gender */}
              <Form.Item
                name="gender"
                label="Gender"
                rules={[
                  { required: true, message: "Please select your gender" },
                ]}
              >
                <Select placeholder="Select gender">
                  <Option value="1">Male</Option>
                  <Option value="2">Female</Option>
                  <Option value="0">Prefer not to say</Option>
                </Select>
              </Form.Item>
            </Col>

            {/* Right Column */}
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              {/* Date of Birth */}
              <Form.Item
                name="dob"
                label="Date of Birth"
                rules={[
                  {
                    required: true,
                    message: "Please select your date of birth",
                  },
                ]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>

              {/* NIC/Passport No */}
              <Form.Item
                name="nic_passport"
                label="NIC / Passport No"
                rules={[
                  {
                    required: true,
                    message: "Please enter your NIC/Passport number",
                  },
                ]}
              >
                <Input placeholder="NIC / Passport Number" />
              </Form.Item>

              {/* Designation */}
              <Form.Item name="designation" label="Designation">
                <Input placeholder="Designation" />
              </Form.Item>

              {/* Department */}
              <Form.Item
                name="department"
                label="Department"
                rules={[
                  { required: true, message: "Please enter your department" },
                ]}
              >
                <Select
                  placeholder={lang.get("global", ["Department"])}
                  showSearch
                  filterOption={false}
                  onInputKeyDown={(e) =>
                    CORE_FUNCTIONS.searchOptions(
                      e.currentTarget.value,
                      "global",
                      "departments",
                      setDepartments
                    )
                  }
                  onClick={() =>
                    CORE_FUNCTIONS.searchOptions(
                      "",
                      "global",
                      "departments",
                      setDepartments
                    )
                  }
                  options={departments.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                />
              </Form.Item>

              {/* Work Location */}
              <Form.Item name="work_location" label="Work Location">
                <Select placeholder="Select work location">
                  <Option value="Head Office">Head Office</Option>
                  <Option value="Branch Office">Branch Office</Option>
                  <Option value="Remote">Remote</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>

              {/* Default Language */}
              <Form.Item
                name="language"
                label="Default Language"
                rules={[
                  {
                    required: true,
                    message: "Please select your preferred language",
                  },
                ]}
              >
                <Select
                  placeholder={lang.get("global", ["Language"])}
                  showSearch
                  filterOption={false}
                  onInputKeyDown={(e) =>
                    CORE_FUNCTIONS.searchOptions(
                      e.currentTarget.value,
                      "global",
                      "languages",
                      setLanguages
                    )
                  }
                  onClick={() =>
                    CORE_FUNCTIONS.searchOptions(
                      "",
                      "global",
                      "languages",
                      setLanguages
                    )
                  }
                  options={languages.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col xs={24} sm={24} md={24} lg={24} xxl={24} xl={24}>
              {/* Signature Upload */}
              <Form.Item
                label="Signature"
                name="signature"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  return normFile(e, "signature");
                }}
              >
                <Upload
                  style={{ width: "100%" }}
                  name="signature"
                  listType="picture-card"
                  showUploadList={false}
                  beforeUpload={() => false}
                >
                  {signatureImage ? (
                    <img
                      src={signatureImage}
                      alt="signature"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <div style={{ width: "100%" }}>
                      <UploadOutlined style={{ fontSize: "32px" }} />
                      <div style={{ marginTop: 8, width: "100%" }}>
                        Upload Signature
                      </div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Form.Item>
            <Button type="primary" loading={saving} htmlType="submit">
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      )}
    </div>
  );
};

export default ProfileSettings;
